import { useEffect, useRef, useState, type RefObject } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowUp, Shuffle } from "lucide-react";
import { hive } from "../../lib/apiClient";
import { randomPublicGame } from "../../lib/catalogue";
import { playablePath } from "../../lib/playableKind";
import { track } from "../../lib/analytics";
import { Spinner } from "../ui/Spinner";

export function CatalogueActions({ scrollTarget, excludeId }: { scrollTarget?: RefObject<HTMLElement | null>; excludeId?: string }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [busy, setBusy] = useState(false);
	const [failed, setFailed] = useState(false);
	const mounted = useRef(false);
	useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

	async function randomGame() {
		if (busy) return;
		setBusy(true);
		setFailed(false);
		try {
			const page = await hive.feed.forYou({ kind: "game", limit: 100 });
			let game = randomPublicGame(page.items.filter((item) => item.id !== excludeId));
			if (!game) game = randomPublicGame((await hive.feed.forYou({ kind: "game", sort: "hot", limit: 100 })).items.filter((item) => item.id !== excludeId));
			if (!game) throw new Error("No game available");
			if (!mounted.current) return;
			track("content_opened", { content_id: game.id, content_kind: "game", source: "random_game" });
			navigate(playablePath(game));
		} catch { if (mounted.current) setFailed(true); }
		finally { if (mounted.current) setBusy(false); }
	}

	return <div className="catalogue-bottom">
		<div className="catalogue-actions">
			<button type="button" className="catalogue-random" disabled={busy} onClick={() => void randomGame()}>
				{busy ? <Spinner size={17} /> : <Shuffle size={17} />}{t("discover.randomGame")}
			</button>
			<button type="button" className="catalogue-top" onClick={() => (scrollTarget?.current ?? window).scrollTo({ top: 0,
				behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" })}>
				<ArrowUp size={17} />{t("discover.backToTop")}
			</button>
		</div>
		{failed ? <p role="alert" className="mt-3 text-center text-sm text-muted">{t("discover.randomFailed")}</p> : null}
	</div>;
}
